import { GlobalContext } from 'api/GlobalContext';
import Header from 'components/Headers/Header.js';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable'; // Import CreatableSelect for better handling of new entries
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Badge,
  Button,
  Card,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from 'reactstrap';
import { getScoreSurfaceLabel } from '../../components/Misc/complianceUtils.js';
import { fetchUserDetails } from '../../components/Misc/apiUtils';
import LoadingBanner from '../../components/Misc/LoadingBanner.js';
import { AnalyticsDashboardContent } from '../AnalyticsDashboard.js';
import AdoptionDashboard from './AdoptionDashboard';
import BillingRules from './BillingRules';
import ComplianceGuidelines from './ComplianceGuidelines';
import CompanyNoteSettings from './CompanyNoteSettings';
import VendorAppsAdmin from './VendorAppsAdmin';
import vendorStore, { isVendorAppsEnabled } from '../../utils/vendorApps/store';

// Tabs that change note output. Hidden from mini_admin; company note
// settings stay with the Confluent admin (Buck).
const NOTE_OUTPUT_TABS = ['compliance', 'companyNoteSettings', 'billingRules'];

// Mirrors backend countPaidSeats: coordinators are free, plus 1 free .edu per account or per 5 paid users
const countPaidSeats = (users, newEmails = []) => {
  const emails = users
    .filter((u) => u.role !== 'Coordinator')
    .map((u) => u.email || '')
    .concat(newEmails);
  const students = emails.filter((e) =>
    e.trim().toLowerCase().endsWith('.edu'),
  ).length;
  const others = emails.length - students;
  return others + Math.max(0, students - Math.max(1, Math.floor(others / 5)));
};

// The sidebar's Admin group (COM-471) splits the old Admin Settings tabs into
// pages. No section = legacy /admin/settings with every tab.
const SECTION_TABS = {
  team: ['users', 'vendorApps'],
  insights: ['analytics', 'adoption'],
  guidelines: ['compliance', 'billingRules', 'companyNoteSettings'],
};
const SECTION_TITLES = { insights: 'Insights', guidelines: 'Clinic Guidelines' };

// Argon's secondary badge is near-white text; the design's User badge is blue.
const USER_BADGE_STYLE = { backgroundColor: '#E8F1FB', color: '#24527F' };
const LABELS_COLLAPSED_COUNT = 3; // rest go behind "+N more" so rows stay short

// What each role can do, shown in the invite modal.
export const ROLE_DEFINITIONS = [
  { role: 'User', label: 'User', desc: 'Documents visits with Comprehend. Uses a seat.' },
  { role: 'Coordinator', label: 'Coordinator (no seat)', desc: 'Manages the team, billing, insights and clinic guidelines. Doesn’t document visits. No seat.' },
  { role: 'Admin', label: 'Admin', desc: 'Everything a User can do, plus team, billing, insights and clinic guidelines. Uses a seat.' },
];
// [permission, User, Admin, Coordinator]
const ROLE_MATRIX = [
  ['Uses a seat', true, true, false],
  ['Documents visits', true, true, false],
  ['Team & billing', false, true, true],
  ['Insights & Clinic Guidelines', false, true, true],
];
const RoleMark = ({ yes }) => (yes ? (
  <svg role="img" aria-label="Yes" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F8A47" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>
) : (
  <svg role="img" aria-label="No" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9AA3B5" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
));

const AdminPortal = ({ section } = {}) => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const inSection = (tab) => !section || SECTION_TABS[section].includes(tab);
  // Vendor Apps flag, re-read when the profile lands: this page usually mounts
  // before /v1/user/stats has answered (a cold side panel opened straight into
  // Admin Settings), and it does not otherwise re-render, so a one-time read
  // would hide the Apps tab for the whole visit.
  const [vendorAppsOn, setVendorAppsOn] = useState(() => isVendorAppsEnabled(apiClient));
  useEffect(() => {
    const recheck = () => setVendorAppsOn(isVendorAppsEnabled(apiClient));
    recheck();
    const offDev = vendorStore.on('devMode', recheck);
    if (apiClient?.onProfileUpdated) apiClient.onProfileUpdated(recheck);
    return () => {
      offDev();
      if (apiClient?.offProfileUpdated) apiClient.offProfileUpdated(recheck);
    };
  }, [apiClient]);
  // "Scorecard" when the scorecard flag is on, "Compliance" otherwise. The tab's
  // internal key stays 'compliance' so deep links (#compliance) keep working.
  const surfaceLabel = getScoreSurfaceLabel(apiClient);
  const location = useLocation();
  const [users, setUsers] = useState([]); // List of users fetched from the API
  const [seats, setSeats] = useState(0); // Total number of seats available
  // Initialize mainTab from hash if present, otherwise default to 'users'
  const [mainTab, setMainTab] = useState(() => {
    const hash = location.hash.substring(1);
    // Check if hash starts with 'compliance' (may have query params like 'compliance?guidelineId=123')
    if (hash.startsWith('compliance')) return 'compliance';
    if (hash === 'analytics') return 'analytics';
    if (hash === 'adoption') return 'adoption';
    // Vendor Apps deep link: '#vendorApps?add=https://vendor.example/comprehend' (see views/VendorApps.js)
    if (hash.startsWith('vendorApps')) return 'vendorApps';
    return 'users';
  });
  // Keep the tab inside this page's section (e.g. /admin/insights opens on Analytics)
  useEffect(() => {
    if (!inSection(mainTab)) setMainTab(SECTION_TABS[section][0]);
  }, [section, mainTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [activeTab, setActiveTab] = useState('active'); // Currently selected tab ('active' or 'pending')
  const [allUsersCount, setAllUsersCount] = useState(0); // Total number of users for display
  const [dropdownOpen, setDropdownOpen] = useState([]); // Tracks dropdown open state for each user
  const [inviteModal, setInviteModal] = useState(false); // State to manage invite modal visibility
  const [emailAddresses, setEmailAddresses] = useState([]); // Stores email addresses for inviting users
  const [newSeatsNeeded, setNewSeatsNeeded] = useState(0); // Number of new seats needed when capacity is exceeded
  const [message, setMessage] = useState(''); // Message displayed in various modals
  const [actionNeeded, setActionNeeded] = useState(''); // Could be 'invite', 'addAndInvite', or 'checkSeats'
  const [roleModalOpen, setRoleModalOpen] = useState(false); // Controls visibility of the role change modal
  const [currentUser, setCurrentUser] = useState(null); // Current user selected for role change
  const [removeModalOpen, setRemoveModalOpen] = useState(false); // Controls visibility of the remove user modal
  const [userToRemove, setUserToRemove] = useState(null); // User selected for removal
  const [allUsers, setAllUsers] = useState([]); // Add this line to define allUsers state
  const [aiThinking, setAiThinking] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [pendingRoleChange, setPendingRoleChange] = useState(null); // Store pending role change details
  const [inviteRole, setInviteRole] = useState('User'); // 'User' or 'Coordinator'
  const [userRole, setUserRole] = useState(null); // Caller's own role, drives the mini admin restriction

  // Label management state
  const [contextMenu, setContextMenu] = useState(null);
  const [labelToDelete, setLabelToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const [addLabelModalOpen, setAddLabelModalOpen] = useState(false);
  const [userForLabels, setUserForLabels] = useState(null);
  const [newLabelsInput, setNewLabelsInput] = useState('');
  const [expandedLabels, setExpandedLabels] = useState({}); // email -> show all labels

  // Handle hash changes to switch tabs (e.g., from compliance score edit button)
  useEffect(() => {
    const hash = location.hash.substring(1);
    // Check if hash starts with 'compliance' (may have query params like 'compliance?guidelineId=123')
    if (hash.startsWith('compliance')) {
      setMainTab('compliance');
    } else if (hash === 'analytics' || hash === 'adoption') {
      setMainTab(hash);
    }
  }, [location.hash, location.key]);

  // mini_admin (Confluent mini admin) gets user management + analytics
  // only. The backend is the real gate — these endpoints reject the role — so
  // this just keeps them from seeing tabs that would only ever error. It fails
  // open on purpose: fetchUserDetails swallows errors, and showing an Admin one
  // extra tab beats hiding tabs from every Admin when /v1/user/stats hiccups.
  useEffect(() => {
    fetchUserDetails(apiClient).then((data) => setUserRole(data?.role));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isMiniAdmin = userRole === 'mini_admin';
  const visibleTabs = (section ? SECTION_TABS[section] : Object.values(SECTION_TABS).flat())
    .filter((tab) => tab !== 'vendorApps' || vendorAppsOn)
    .filter((tab) => !(isMiniAdmin && NOTE_OUTPUT_TABS.includes(tab)));

  // A mini admin can't demote or remove an Admin/Coordinator (the backend refuses
  // it), so don't offer the action — the 201 denial resolves rather than rejects,
  // which would otherwise pop a success toast for a change that never happened.
  const isNoteSettingsOwner = (user) =>
    isMiniAdmin &&
    (user.role === 'Admin' || user.role === 'Coordinator');

  useEffect(() => {
    if (
      (isMiniAdmin && section === 'guidelines') ||
      (userRole === 'Coordinator' && (section === 'insights' || section === 'guidelines'))
    ) {
      navigate('/User/admin/team', { replace: true });
    } else if (isMiniAdmin && NOTE_OUTPUT_TABS.includes(mainTab)) {
      setMainTab(section ? SECTION_TABS[section][0] : 'users');
    }
  }, [userRole, mainTab, section]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggles the dropdown open state for the specified user index
  const toggleDropdown = (index) => {
    let newArray = [...dropdownOpen];
    newArray[index] = !newArray[index];
    setDropdownOpen(newArray);
  };

  const toggleInviteModal = () => setInviteModal(!inviteModal);

  const handleEmailChange = (value) => {
    setEmailAddresses(value || []);
  };

  const convertInputToEmails = (inputText) => {
    if (inputText.trim()) {
      const emails = inputText.split(/[,\s]+/).filter((email) => email.trim());
      const newEmails = emails
        .map((email) => ({ value: email.trim(), label: email.trim() }))
        .filter(
          (newEmail) =>
            !emailAddresses.find(
              (existing) => existing.value === newEmail.value,
            ),
        );
      if (newEmails.length > 0) {
        handleEmailChange([...emailAddresses, ...newEmails]);
      }
      setInputValue('');
    }
  };

  // API calls
  // Fetches subscription data from the API
  const fetchGroupSubscriptionStats = () => {
    apiClient
      .getGroupSubscriptionStats({})
      .then((data) => {
        const userArray = Object.entries(data.users).map(
          ([email, details]) => ({
            ...details,
            email: email,
            isActive: details.isActive,
          }),
        );
        setAllUsers(userArray); // Set all users fetched from the API
        setUsers(
          userArray.filter((user) =>
            activeTab === 'active' ? user.isActive : !user.isActive,
          ),
        );
        setSeats(data.seats);
        setAllUsersCount(countPaidSeats(userArray));
        setDropdownOpen(new Array(userArray.length).fill(false));
        filterUsers(activeTab, userArray); // Filter users initially
      })
      .catch((error) => {
        console.error('Error fetching group subscription stats:', error);
      });
  };

  const filterUsers = (tab, users) => {
    const filtered = users.filter((user) =>
      tab === 'active' ? user.isActive : !user.isActive,
    );
    setUsers(filtered);
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    filterUsers(activeTab, allUsers);
  }, [activeTab, allUsers]); // Re-filter when activeTab changes or allUsers is updated

  // Handle label right-click (context menu)
  const handleLabelRightClick = (e, label, userEmail) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      label,
      userEmail,
    });
  };

  // Handle single label delete
  const handleDeleteLabel = (userEmail, label) => {
    setLabelToDelete({ userEmail, label, type: 'single' });
    setDeleteModalOpen(true);
    setContextMenu(null);
  };

  // Handle delete label from all users
  const handleDeleteLabelFromAll = (label) => {
    setLabelToDelete({ label, type: 'all' });
    setDeleteAllModalOpen(true);
    setContextMenu(null);
  };

  // Confirm single label deletion
  const confirmDeleteLabel = async () => {
    if (!labelToDelete) return;

    try {
      const response = await apiClient.removeLabelFromUser({
        email: labelToDelete.userEmail,
        label: labelToDelete.label,
      });

      if (response.result === 'success') {
        toast.success(`Label removed from ${labelToDelete.userEmail}`);
        fetchGroupSubscriptionStats(); // Refresh the user list
      } else {
        toast.error('Failed to remove label');
      }
    } catch (error) {
      console.error('Error removing label:', error);
      toast.error('Failed to remove label');
    } finally {
      setDeleteModalOpen(false);
      setLabelToDelete(null);
    }
  };

  // Confirm delete label from all users
  const confirmDeleteLabelFromAll = async () => {
    if (!labelToDelete) return;

    try {
      const response = await apiClient.removeLabelFromAllUsers({
        label: labelToDelete.label,
      });

      if (response.result === 'success') {
        const { summary } = response;
        toast.success(
          `Label removed from ${summary.success} user${summary.success !== 1 ? 's' : ''}`,
        );
        fetchGroupSubscriptionStats(); // Refresh the user list
      } else {
        toast.error('Failed to remove label from all users');
      }
    } catch (error) {
      console.error('Error removing label from all users:', error);
      toast.error('Failed to remove label from all users');
    } finally {
      setDeleteAllModalOpen(false);
      setLabelToDelete(null);
    }
  };

  // Open add labels modal for a user
  const handleAddLabelsClick = (user) => {
    setUserForLabels(user);
    setNewLabelsInput('');
    setAddLabelModalOpen(true);
  };

  // Handle adding labels manually
  const handleAddLabelsSubmit = async () => {
    if (!userForLabels || !newLabelsInput.trim()) {
      toast.error('Please enter at least one label');
      return;
    }

    // Parse labels (semicolon-separated)
    const labels = newLabelsInput
      .split(';')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (labels.length === 0) {
      toast.error('Please enter valid labels separated by semicolons');
      return;
    }

    try {
      const response = await apiClient.bulkAddLabels({
        users: [
          {
            email: userForLabels.email,
            labels: labels,
          },
        ],
      });

      if (response.result === 'success' && response.summary.success > 0) {
        const detail = response.details[0];
        if (detail.labels_added.length > 0) {
          toast.success(
            `Added ${detail.labels_added.length} label${detail.labels_added.length !== 1 ? 's' : ''} to ${userForLabels.email}`,
          );
        } else {
          toast.info('All labels were already present on this user');
        }
        setAddLabelModalOpen(false);
        setUserForLabels(null);
        setNewLabelsInput('');
        fetchGroupSubscriptionStats(); // Refresh the user list
      } else {
        toast.error('Failed to add labels. Check console for details.');
        console.log('Add labels response:', response);
      }
    } catch (error) {
      console.error('Error adding labels:', error);
      toast.error('Failed to add labels');
    }
  };

  // Change and save new role for the specified user
  const handleSaveNewRole = (user, newRole) => {
    // Check if role change would exceed seat capacity
    const willExceedCapacity =
      user.role === 'Coordinator' &&
      newRole !== 'Coordinator' &&
      countPaidSeats(
        allUsers.map((u) => (u === user ? { ...u, role: newRole } : u)),
      ) > seats;

    if (willExceedCapacity) {
      // Store the pending role change and show invite popup with pre-populated email
      setPendingRoleChange({ user, newRole });
      setRoleModalOpen(false);
      setEmailAddresses([{ value: user.email, label: user.email }]); // Pre-populate with user's email
      setNewSeatsNeeded(1);
      fetchSeatEstimate(1);
      setActionNeeded('addAndInvite');
      setInviteModal(true);
      return;
    }

    // Proceed with role change if seats are available
    apiClient
      .changeUserRole(user.email, newRole)
      .then((response) => {
        if (response.error) {
          toast.error(response.error);
          return;
        }
        fetchGroupSubscriptionStats(); // Refresh data to reflect role change
        setRoleModalOpen(false);
        toast.success(
          `Role successfully changed to ${newRole === 'User' ? 'User' : newRole === 'mini_admin' ? 'Team Manager' : newRole}`,
        );
      })
      .catch((error) => {
        console.error('Failed to update role:', error);
        toast.error('Failed to update role. Please try again.');
      });
  };

  const emails = emailAddresses.map((email) => email.value).join(',');

  // Add more seats to subscription, charge them with Stripe and invite the users with the specified email addresses
  const addMoreSeats = () => {
    setAiThinking(true);
    const isRoleChange = pendingRoleChange !== null;
    setLoadingText(
      isRoleChange
        ? 'Adding seat and changing role...'
        : 'Adding seats and Inviting users...',
    );

    apiClient
      .addMoreSeats(emails, newSeatsNeeded)
      .then((response) => {
        setAiThinking(false);
        if (response.error) {
          console.error('Error adding more seats:', response.error);
          alert(`Failed to add more seats: ${response.error}`);
        } else {
          if (isRoleChange && pendingRoleChange) {
            // Complete the pending role change
            apiClient
              .changeUserRole(
                pendingRoleChange.user.email,
                pendingRoleChange.newRole,
              )
              .then(() => {
                setEmailAddresses([]); // Clear the email addresses
                setPendingRoleChange(null); // Clear pending role change
                toggleInviteModal(); // Close the modal
                fetchGroupSubscriptionStats(); // Refetch subscription stats to update the UI
                toast.success(
                  `Seat added and role successfully changed to ${pendingRoleChange.newRole === 'User' ? 'User' : pendingRoleChange.newRole === 'mini_admin' ? 'Team Manager' : pendingRoleChange.newRole}`,
                );
              })
              .catch((error) => {
                console.error(
                  'Failed to update role after adding seat:',
                  error,
                );
                toast.error(
                  'Seat added but failed to update role. Please try changing the role again.',
                );
                setEmailAddresses([]);
                setPendingRoleChange(null);
                toggleInviteModal();
                fetchGroupSubscriptionStats();
              });
          } else {
            // Normal invite flow
            setEmailAddresses([]); // Clear the email addresses after successful invite
            toggleInviteModal(); // Close the modal
            fetchGroupSubscriptionStats(); // Refetch subscription stats to update the UI
            setLoadingText('Success!! Added Seats and Invited users');
          }
        }
      })
      .catch((error) => {
        setAiThinking(false);
        console.error('Failed to add more seats:', error);
        alert(
          'Failed to add more seats: Check your network connection and try again.',
        );
      });
  };

  // Invite the users with the specified email addresses without needing to buy more seats
  const inviteUsers = () => {
    apiClient
      .inviteUsers(emails, inviteRole)
      .then((response) => {
        if (response.error) {
          toast.error(response.error);
          return;
        }
        console.log('User added to subscription successfully');

        // Show toast based on API response
        if (response.result === 'success') {
          const existingCount = response.existing_users
            ? response.existing_users.length
            : 0;
          const newCount = response.new_users ? response.new_users.length : 0;

          let toastMessage = '';
          if (existingCount > 0 && newCount > 0) {
            toastMessage = `${existingCount} existing user(s) added to your subscription and ${newCount} new user(s) invited`;
          } else if (existingCount > 0) {
            toastMessage = `${existingCount} existing user(s) added to your subscription`;
          } else if (newCount > 0) {
            toastMessage = `${newCount} new user(s) invited`;
          } else {
            toastMessage = 'Users processed successfully';
          }

          toast.success(toastMessage);
        }

        setEmailAddresses([]); // Clear the email addresses after successful invite
        setInviteRole('User');
        // Close the modal
        toggleInviteModal();
        // Refetch subscription stats to update the UI
        fetchGroupSubscriptionStats();
        // Handle successful user addition
      })
      .catch((error) => {
        console.error('Failed to Invite users:', error);
        toast.error('Failed to invite users. Please try again.');
      });
  };

  // Fetch the prorated amount for each additional seat
  const fetchSeatEstimate = (additionalSeats) => {
    apiClient
      .getNewSubEstimate(additionalSeats)
      .then((estimate) => {
        if (estimate.error) {
          setMessage(
            `Failed to fetch seat estimate - ${estimate.error}. Contact: support@comprehendpt.ai`,
          );
        } else {
          if (estimate.reseller_managed) {
            // Backend provides a friendly reseller message; surface it verbatim
            setMessage(
              estimate.message ||
                `These ${additionalSeats} additional seats will be billed by ${estimate.reseller_name || 'your reseller'} at your existing rate.`,
            );
          } else {
            setMessage(
              `Adding ${additionalSeats} more seats will result in a prorated charge of $${estimate.prorated_amount} today, based on a unit price of $${estimate.unit_price} per seat. This amount will be charged to the card we have on file.`,
            );
          }
        }
      })
      .catch((error) => {
        console.error('Failed to fetch seat estimate:', error);
        // Properly set the error message here within the catch block
        setMessage(
          'Failed to fetch seat estimate - Ensure correct email is associated with Comprehend subscription or Stripe. Contact: support@comprehendpt.ai',
        );
      });
  };

  // Send a reminder email to the specified user
  const handleSendReminder = (email) => {
    apiClient
      .sendUserEmailReminder(email)
      .then(() => {
        alert(`Email successfully sent to: ${email}.`);
      })
      .catch((error) => {
        alert(`Unable to send emal to: ${email}.`);
      });
  };

  // Remove the user with the specified email from the subscription
  const confirmRemoveUser = (email) => {
    apiClient
      .removeUser(email)
      .then((response) => {
        if (response.error) {
          toast.error(response.error);
          return;
        }
        setRemoveModalOpen(false);
        toast.success(
          `${email} has been removed successfully, billing settings have not changed.`,
        );
        fetchGroupSubscriptionStats(); // Refresh list to reflect the removal
      })
      .catch((error) => {
        console.error('Error removing user:', error);
      });
  };

  useEffect(() => {
    fetchGroupSubscriptionStats();
  }, []); // Fetch on the first mount

  // Depend on emailAddresses, seats, and allUsersCount
  useEffect(() => {
    // Whenever emails change and if the seatEstimate is already present, re-check available seats
    if (emailAddresses.length === 0 || emailAddresses.some((email) => email)) {
      checkAvailableSeats();
    }
  }, [emailAddresses, seats, allUsersCount, allUsers, inviteRole]);

  useEffect(() => {
    if (inviteRole === 'Coordinator') return; // handled by checkAvailableSeats
    const newSeatCount =
      countPaidSeats(
        allUsers,
        emailAddresses.map((e) => e.value),
      ) - allUsersCount;
    if (newSeatCount <= seats - allUsersCount) {
      setMessage(
        `You have ${seats - allUsersCount - newSeatCount} seats available.`,
      );
      setActionNeeded('invite');
    } else {
      setActionNeeded('addAndInvite');
    }
  }, [emailAddresses, seats, allUsersCount, allUsers, inviteRole]);

  const ChangeRoleModal = ({ isOpen, toggle, user, onSave }) => {
    const [selectedRole, setSelectedRole] = useState(user ? user.role : 'User');
    return (
      <Modal isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>Modify Role for {user.name}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="roleSelect">Modify Role</Label>
            <Input
              type="select"
              id="roleSelect"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="User">User</option>
              {/* A mini admin can't grant a role that edits note output — the
                  backend rejects it too, this just keeps the menu honest. */}
              {!isMiniAdmin && <option value="Admin">Admin</option>}
              {!isMiniAdmin && (
                <option value="Coordinator">Coordinator</option>
              )}
              {apiClient.isConfluentUser && (
                <option value="mini_admin">
                  Team Manager
                </option>
              )}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => onSave(user, selectedRole)}>
            Apply
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    );
  };
  const handleRoleChangeClick = (user) => {
    setCurrentUser(user);
    setRoleModalOpen(true);
  };

  const RemoveUserModal = ({ isOpen, toggle, user, onConfirm }) => {
    const isSelf = user.email === apiClient.sessionEmail; // Check if the user's email matches the current subscription email
    return (
      <Modal isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>Confirm Removal</ModalHeader>
        <ModalBody>
          {isSelf
            ? 'You cannot remove yourself from the subscription.'
            : `Removing "${user.name ? user.name : user.email}" from your organization. It will free up 1 seat for your subscription plan.`}
        </ModalBody>
        <ModalFooter>
          {!isSelf && (
            <Button color="danger" onClick={() => onConfirm(user.email)}>
              Remove
            </Button>
          )}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    );
  };
  const handleRemoveClick = (user) => {
    setUserToRemove(user);
    setRemoveModalOpen(true);
  };

  const handleCancelInvite = () => {
    setEmailAddresses([]); // Clear the email addresses when cancelling
    setPendingRoleChange(null); // Clear any pending role change
    setInviteRole('User');
    setNewSeatsNeeded(0); // Clear seats needed count
    setMessage(''); // Clear any messages
    setActionNeeded(''); // Clear action needed state
    toggleInviteModal(); // Close the modal
  };

  const checkAvailableSeats = () => {
    if (inviteRole === 'Coordinator') {
      setMessage('Coordinators do not use a paid seat.');
      setActionNeeded('invite');
      return;
    }
    const newEmailCount =
      countPaidSeats(
        allUsers,
        emailAddresses.map((e) => e.value),
      ) - allUsersCount;
    const availableSeats = seats - allUsersCount;
    if (availableSeats < newEmailCount) {
      const seatsNeeded = newEmailCount - availableSeats;
      setNewSeatsNeeded(seatsNeeded);
      fetchSeatEstimate(seatsNeeded);
      setActionNeeded('addAndInvite');
    } else {
      // If enough seats are available
      setMessage(`You have ${availableSeats} seats available.`);
      setActionNeeded('invite');
    }
  };

  // Main tabs within Admin Portal (User Management and Compliance)

  return (
    <>
      <Header isLite={true} />
      {/* Page content */}
      <Container className="mt--7" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <div className="mt-3 mb-3 mr-3 ml-3"></div>
              {SECTION_TITLES[section] && (
                <h1 style={{ margin: '0 20px 16px', fontSize: 28, fontWeight: 700, color: '#262C4D' }}>{SECTION_TITLES[section]}</h1>
              )}

              {/* Main Tab Navigation (hidden when the page has a single tab) */}
              {visibleTabs.length > 1 && (
              <div
                style={{
                  marginLeft: '20px',
                  marginRight: '20px',
                  marginBottom: '20px',
                }}
              >
                <Nav tabs>
                  {inSection('users') && (
                  <NavItem>
                    <NavLink
                      active={mainTab === 'users'}
                      onClick={() => setMainTab('users')}
                      style={{ cursor: 'pointer' }}
                    >
                      User Management
                    </NavLink>
                  </NavItem>
                  )}
                  {inSection('analytics') && (
                  <NavItem>
                    <NavLink
                      active={mainTab === 'analytics'}
                      onClick={() => setMainTab('analytics')}
                      style={{ cursor: 'pointer' }}
                    >
                      Analytics
                    </NavLink>
                  </NavItem>
                  )}
                  {!isMiniAdmin && inSection('compliance') && (
                    <NavItem>
                      <NavLink
                        active={mainTab === 'compliance'}
                        onClick={() => setMainTab('compliance')}
                        style={{ cursor: 'pointer' }}
                      >
                        {surfaceLabel} Guidelines
                      </NavLink>
                    </NavItem>
                  )}
                  {inSection('adoption') && (
                  <NavItem>
                    <NavLink
                      active={mainTab === 'adoption'}
                      onClick={() => setMainTab('adoption')}
                      style={{ cursor: 'pointer' }}
                    >
                      Adoption
                    </NavLink>
                  </NavItem>
                  )}
                  {!isMiniAdmin && inSection('billingRules') && (
                    <NavItem>
                      <NavLink
                        active={mainTab === 'billingRules'}
                        onClick={() => setMainTab('billingRules')}
                        style={{ cursor: 'pointer' }}
                      >
                        Coding Guidelines
                      </NavLink>
                    </NavItem>
                  )}
                  {!isMiniAdmin && inSection('companyNoteSettings') && (
                    <NavItem>
                      <NavLink
                        active={mainTab === 'companyNoteSettings'}
                        onClick={() => setMainTab('companyNoteSettings')}
                        style={{ cursor: 'pointer' }}
                      >
                        Clinic-wide Note Settings
                      </NavLink>
                    </NavItem>
                  )}
                  {vendorAppsOn && inSection('vendorApps') && (
                    <NavItem>
                      <NavLink
                        active={mainTab === 'vendorApps'}
                        onClick={() => setMainTab('vendorApps')}
                        style={{ cursor: 'pointer' }}
                      >
                        Apps
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>
              </div>
              )}

              {/* Render User Management Tab */}
              {mainTab === 'users' && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '20px',
                      marginLeft: '20px',
                      marginRight: '20px',
                    }}
                  >
                    <div>
                      <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#262C4D' }}>Subscribed Users</h1>
                      <div style={{ fontSize: 16, marginTop: 4, color: '#3B4260' }}>
                        {allUsersCount}/{seats} seats occupied
                      </div>
                    </div>
                    <div className="button-stack">
                      <Button
                        color="info"
                        size="sm"
                        onClick={() =>
                          inSection('adoption')
                            ? setMainTab('adoption')
                            : navigate('/User/admin/insights#adoption')
                        }
                      >
                        <i
                          className="fa fa-tags"
                          style={{ marginRight: '8px' }}
                        ></i>
                        Add Labels
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={toggleInviteModal}
                      >
                        <i
                          className="fa fa-user-plus"
                          style={{ marginRight: '8px' }}
                        ></i>
                        Invite Users
                      </Button>
                    </div>
                  </div>
                  {apiClient.groupSubscriptionId && !apiClient.EMR?.includes('Stride') && (
                    <div
                      style={{
                        marginBottom: '16px',
                        marginLeft: '20px',
                        marginRight: '20px',
                        background: '#f0f7ff',
                        border: '1px solid #c0d8f0',
                        borderRadius: '6px',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#1a6fa8',
                          }}
                        >
                          <i
                            className="fa fa-link"
                            style={{ marginRight: '6px' }}
                          />
                          Team Invite Link
                        </div>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#555',
                            marginTop: '2px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {`https://app.comprehendpt.com/auth/register?referralCode=group-${apiClient.groupSubscriptionId}`}
                        </div>
                      </div>
                      <Button
                        color="primary"
                        size="sm"
                        style={{
                          flexShrink: 0,
                          fontSize: '12px',
                          fontWeight: '600',
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `https://app.comprehendpt.com/auth/register?referralCode=group-${apiClient.groupSubscriptionId}`,
                          );
                          toast.success('Invite link copied!');
                        }}
                      >
                        <i
                          className="fa fa-copy"
                          style={{ marginRight: '4px' }}
                        />
                        Copy
                      </Button>
                    </div>
                  )}
                  <div style={{ marginTop: '20px' }}>
                    <Nav tabs>
                      <NavItem>
                        <NavLink
                          active={activeTab === 'active'}
                          onClick={() => setActiveTab('active')}
                        >
                          Active
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          active={activeTab === 'pending'}
                          onClick={() => setActiveTab('pending')}
                        >
                          Pending
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                  <div className="table-responsive-wrapper">
                    <Table striped>
                      <thead>
                        <tr>
                          <th>Actions</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Labels</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={user.email}>
                            <td>
                              <Dropdown
                                isOpen={dropdownOpen[index]}
                                toggle={() => toggleDropdown(index)}
                              >
                                <DropdownToggle
                                  caret={false}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: 'black',
                                  }}
                                >
                                  <i
                                    className="fa fa-ellipsis-v"
                                    aria-hidden="true"
                                    size="sm"
                                  ></i>
                                </DropdownToggle>
                                <DropdownMenu>
                                  {activeTab === 'pending' && (
                                    <>
                                      <DropdownItem
                                        onClick={() =>
                                          handleSendReminder(user.email)
                                        }
                                      >
                                        <i
                                          className="fa fa-envelope"
                                          aria-hidden="true"
                                        ></i>{' '}
                                        Send Reminder
                                      </DropdownItem>
                                      <DropdownItem divider />
                                    </>
                                  )}

                                  {!isNoteSettingsOwner(user) && (
                                    <>
                                      <DropdownItem
                                        onClick={() => handleRoleChangeClick(user)}
                                      >
                                        <i
                                          className="fa fa-pen"
                                          aria-hidden="true"
                                        ></i>{' '}
                                        Change Role
                                      </DropdownItem>
                                      <DropdownItem divider />
                                    </>
                                  )}
                                  <DropdownItem
                                    onClick={() => handleAddLabelsClick(user)}
                                  >
                                    <i
                                      className="fa fa-tags"
                                      aria-hidden="true"
                                    ></i>{' '}
                                    Add Labels
                                  </DropdownItem>
                                  {!isNoteSettingsOwner(user) && (
                                    <>
                                      <DropdownItem divider />
                                      <DropdownItem
                                        onClick={() => handleRemoveClick(user)}
                                      >
                                        <i
                                          className="fa fa-trash"
                                          aria-hidden="true"
                                        ></i>{' '}
                                        Remove
                                      </DropdownItem>
                                    </>
                                  )}
                                </DropdownMenu>
                              </Dropdown>
                            </td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>
                              <Badge
                                color={
                                  user.role === 'Admin'
                                    ? 'success'
                                    : user.role === 'Coordinator'
                                      ? 'warning'
                                      : 'secondary'
                                }
                                style={user.role === 'Admin' || user.role === 'Coordinator' ? undefined : USER_BADGE_STYLE}
                              >
                                {user.role === 'User'
                                  ? 'User'
                                  : user.role === 'mini_admin'
                                    ? 'Team Manager'
                                    : user.role}
                              </Badge>
                            </td>
                            <td>
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '5px',
                                  // Wide enough that pills wrap across, not stack one per line
                                  minWidth: '320px',
                                  maxWidth: '480px',
                                }}
                              >
                                {(expandedLabels[user.email]
                                  ? user.labels || []
                                  : (user.labels || []).slice(0, LABELS_COLLAPSED_COUNT)
                                ).map((label, labelIdx) => (
                                  <Badge
                                    key={labelIdx}
                                    color="info"
                                    pill
                                    style={{
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '5px',
                                      padding: '5px 10px',
                                    }}
                                    onContextMenu={(e) =>
                                      handleLabelRightClick(
                                        e,
                                        label,
                                        user.email,
                                      )
                                    }
                                  >
                                    {label}
                                    <i
                                      className="fa fa-times"
                                      style={{
                                        fontSize: '10px',
                                        cursor: 'pointer',
                                        marginLeft: '3px',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteLabel(user.email, label);
                                      }}
                                      title="Remove this label"
                                    />
                                  </Badge>
                                ))}
                                {(user.labels || []).length > LABELS_COLLAPSED_COUNT && (
                                  <Badge
                                    color="light"
                                    pill
                                    style={{ cursor: 'pointer', padding: '5px 10px' }}
                                    onClick={() =>
                                      setExpandedLabels((prev) => ({
                                        ...prev,
                                        [user.email]: !prev[user.email],
                                      }))
                                    }
                                    title={expandedLabels[user.email] ? undefined : user.labels.join(', ')}
                                  >
                                    {expandedLabels[user.email]
                                      ? 'Show less'
                                      : `+${user.labels.length - LABELS_COLLAPSED_COUNT} more`}
                                  </Badge>
                                )}
                                {(!user.labels || user.labels.length === 0) && (
                                  <span
                                    style={{
                                      color: '#6c757d',
                                      fontSize: '14px',
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              {/* Render Analytics Tab */}
              {mainTab === 'analytics' && <AnalyticsDashboardContent />}

              {/* Render Compliance Guidelines Tab */}
              {!isMiniAdmin && mainTab === 'compliance' && (
                <div style={{ padding: '0 20px' }}>
                  <ComplianceGuidelines />
                </div>
              )}

              {/* Render Adoption Tab */}
              {mainTab === 'adoption' && (
                <AdoptionDashboard apiClient={apiClient} />
              )}

              {/* Render Company Note Settings Tab */}
              {!isMiniAdmin && mainTab === 'companyNoteSettings' && (
                <div style={{ padding: '0 20px 20px' }}>
                  <CompanyNoteSettings />
                </div>
              )}

              {/* Render Billing Rules Tab */}
              {!isMiniAdmin && mainTab === 'billingRules' && (
                <div style={{ padding: '0 20px 20px' }}>
                  <BillingRules />
                </div>
              )}

              {/* Render Vendor Apps Tab (docs/vendor-apps-contract.md §5) */}
              {mainTab === 'vendorApps' && vendorAppsOn && (
                <div style={{ padding: '0 20px 20px' }}>
                  <VendorAppsAdmin />
                </div>
              )}

              {/* Invite Users Modal */}
              <Modal
                isOpen={inviteModal}
                toggle={toggleInviteModal}
                backdrop="static"
                style={{ maxWidth: 600 }}
              >
                <ModalHeader toggle={toggleInviteModal}>
                  <span style={{ fontSize: 20, fontWeight: 700, color: '#262C4D' }}>
                    {pendingRoleChange ? 'Add 1 more user' : 'Invite users'}
                  </span>
                </ModalHeader>
                <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: 0 }}>
                  {!pendingRoleChange && !isMiniAdmin && (
                    <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: '6px 14px' }}>
                      <div style={{ flex: '0 0 220px' }}>
                        <Label for="inviteRoleSelect" style={{ fontWeight: 600, color: '#262C4D' }}>Role</Label>
                        <Input
                          type="select"
                          id="inviteRoleSelect"
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value)}
                        >
                          {ROLE_DEFINITIONS.map((r) => (
                            <option key={r.role} value={r.role}>{r.label}</option>
                          ))}
                        </Input>
                      </div>
                      <span style={{ flex: '1 1 200px', fontSize: 13, lineHeight: 1.4, color: '#5E6680', paddingBottom: 10 }}>
                        {ROLE_DEFINITIONS.find((r) => r.role === inviteRole)?.desc}
                      </span>
                    </div>
                  )}
                  <FormGroup>
                    <Label for="emailInput" style={{ fontWeight: 600, color: '#262C4D' }}>
                      Add emails you want to invite
                    </Label>
                    <CreatableSelect
                      isMulti
                      onChange={handleEmailChange}
                      options={emailAddresses}
                      value={emailAddresses}
                      inputValue={inputValue}
                      onInputChange={setInputValue}
                      placeholder="Type or paste emails"
                      formatCreateLabel={() => null}
                      noOptionsMessage={() => null}
                      menuIsOpen={false}
                      blurInputOnSelect={false}
                      onBlur={(e) => {
                        // Convert typed text to email chip when clicking outside
                        const currentValue = e.target.value || inputValue;
                        convertInputToEmails(currentValue);
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' ||
                          e.key === ',' ||
                          e.key === ' '
                        ) {
                          e.preventDefault();
                          convertInputToEmails(inputValue);
                        }
                      }}
                    />
                    {message && (
                      <div className="text-right mt-2" style={{ fontSize: 13, color: '#5E6680' }}>{message}</div>
                    )}
                  </FormGroup>
                  {!pendingRoleChange && (
                    <details style={{ fontSize: 14, borderTop: '1px solid #EEF0F4', paddingTop: 12 }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#1D6FB8' }}>What can each role do?</summary>
                      <table style={{ width: '100%', marginTop: 10, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th scope="col"><span className="sr-only">Permission</span></th>
                            {['User', 'Admin', 'Coordinator'].map((r) => (
                              <th key={r} scope="col" style={{ padding: 6, width: '22%', textAlign: 'center', color: '#262C4D' }}>{r}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {ROLE_MATRIX.map(([perm, ...marks]) => (
                            <tr key={perm} style={{ borderTop: '1px solid #EEF0F4' }}>
                              <th scope="row" style={{ padding: '8px 6px 8px 0', fontWeight: 500, color: '#3B4260' }}>{perm}</th>
                              {marks.map((yes, i) => (
                                <td key={i} style={{ padding: '8px 6px', textAlign: 'center' }}><RoleMark yes={yes} /></td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
                </ModalBody>
                <ModalFooter>
                  {actionNeeded === 'invite' && (
                    <>
                      <Button color="secondary" onClick={handleCancelInvite}>
                        Cancel
                      </Button>
                      <Button color="primary" onClick={inviteUsers}>
                        Invite users
                      </Button>
                    </>
                  )}
                  {actionNeeded === 'addAndInvite' && (
                    <>
                      <Button color="secondary" onClick={handleCancelInvite}>
                        Cancel
                      </Button>
                      {!message.includes('Failed to fetch seat estimate') && !apiClient.EMR?.includes('Stride') && (
                        <Button
                          color="primary"
                          onClick={addMoreSeats}
                          disabled={aiThinking ? true : false}
                        >
                          Add seats &amp; invite
                        </Button>
                      )}
                    </>
                  )}
                  {actionNeeded === 'checkSeats' && (
                    <>
                      <Button color="secondary" onClick={handleCancelInvite}>
                        Cancel
                      </Button>
                      <Button color="primary" onClick={checkAvailableSeats}>
                        Check available seats
                      </Button>
                    </>
                  )}
                </ModalFooter>
              </Modal>
              {currentUser && (
                <ChangeRoleModal
                  isOpen={roleModalOpen}
                  toggle={() => setRoleModalOpen(!roleModalOpen)}
                  user={currentUser}
                  onSave={handleSaveNewRole}
                />
              )}
              {userToRemove && (
                <RemoveUserModal
                  isOpen={removeModalOpen}
                  toggle={() => setRemoveModalOpen(!removeModalOpen)}
                  user={userToRemove}
                  onConfirm={confirmRemoveUser}
                />
              )}
            </Card>
          </div>
        </Row>
      </Container>
      <LoadingBanner showBanner={aiThinking} loadingText={loadingText} />

      {/* Context Menu for Labels */}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 1000,
            minWidth: '200px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: '10px 15px',
              cursor: 'pointer',
              borderBottom: '1px solid #eee',
            }}
            onClick={() =>
              handleDeleteLabel(contextMenu.userEmail, contextMenu.label)
            }
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = '#f5f5f5')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'white')
            }
          >
            <i className="fa fa-times" style={{ marginRight: '8px' }}></i>
            Remove from this user
          </div>
          <div
            style={{
              padding: '10px 15px',
              cursor: 'pointer',
              color: '#dc3545',
            }}
            onClick={() => handleDeleteLabelFromAll(contextMenu.label)}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = '#f5f5f5')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'white')
            }
          >
            <i className="fa fa-trash" style={{ marginRight: '8px' }}></i>
            Remove from ALL users
          </div>
        </div>
      )}

      {/* Delete Label Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>
          Confirm Label Removal
        </ModalHeader>
        <ModalBody>
          {labelToDelete && (
            <p>
              Are you sure you want to remove the label "
              <strong>{labelToDelete.label}</strong>" from{' '}
              <strong>{labelToDelete.userEmail}</strong>?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDeleteLabel}>
            Remove Label
          </Button>
          <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Label from All Users Confirmation Modal */}
      <Modal
        isOpen={deleteAllModalOpen}
        toggle={() => setDeleteAllModalOpen(false)}
      >
        <ModalHeader toggle={() => setDeleteAllModalOpen(false)}>
          Confirm Label Removal from ALL Users
        </ModalHeader>
        <ModalBody>
          {labelToDelete && (
            <>
              <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
                ⚠️ Warning: This action will remove the label from ALL users in
                your organization!
              </p>
              <p>
                Are you sure you want to remove the label "
                <strong>{labelToDelete.label}</strong>" from all users who have
                it?
              </p>
              <p style={{ fontSize: '14px', color: '#6c757d' }}>
                This action cannot be undone.
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDeleteLabelFromAll}>
            Remove from ALL Users
          </Button>
          <Button
            color="secondary"
            onClick={() => setDeleteAllModalOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add Labels Modal */}
      <Modal
        isOpen={addLabelModalOpen}
        toggle={() => setAddLabelModalOpen(false)}
      >
        <ModalHeader toggle={() => setAddLabelModalOpen(false)}>
          Add Labels to User
        </ModalHeader>
        <ModalBody>
          {userForLabels && (
            <>
              <p>
                Adding labels to: <strong>{userForLabels.name}</strong> (
                {userForLabels.email})
              </p>

              {userForLabels.labels && userForLabels.labels.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <small style={{ color: '#6c757d' }}>Current labels:</small>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '5px',
                      marginTop: '5px',
                    }}
                  >
                    {userForLabels.labels.map((label, idx) => (
                      <Badge key={idx} color="secondary" pill>
                        {label}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <FormGroup>
                <Label for="labelsInput">
                  New Labels (separate multiple labels with semicolons)
                </Label>
                <Input
                  type="text"
                  id="labelsInput"
                  placeholder="e.g., power_user; needs_training; active"
                  value={newLabelsInput}
                  onChange={(e) => setNewLabelsInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddLabelsSubmit();
                    }
                  }}
                />
                <small
                  style={{
                    color: '#6c757d',
                    marginTop: '5px',
                    display: 'block',
                  }}
                >
                  💡 Use semicolons (;) to separate multiple labels
                </small>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleAddLabelsSubmit}
            disabled={!newLabelsInput.trim()}
          >
            Add Labels
          </Button>
          <Button color="secondary" onClick={() => setAddLabelModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <style>
        {`
          .table-responsive-wrapper {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .table-responsive-wrapper table {
            min-width: 800px;  /* Ensures table won't squish on small screens */
          }

          .button-stack {
            display: flex;
            gap: 10px;
          }

          .dropdown {
            position: static;  /* Change from relative to static */
          }

          @media screen and (max-width: 768px) {
            .button-stack {
              flex-direction: column;
              align-items: stretch;
            }

            .button-stack button {
              width: 100%;
              margin-bottom: 8px;
            }
          }
        `}
      </style>
      <ToastContainer />
    </>
  );
};

export default AdminPortal;
